import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import InputsCentralAssumption from './InputsCentralAssumption';
import InputsClientsGroups from './InputsClientsGroups';
import InputsEnvironmental from './InputsEnvironmental';
import InputsEventExpenditure from './InputsEventExpenditure';
import InputsPrePost from './InputsPrePost';
import InputsReachAwareness from './InputsReachAwareness';
import InputsSustainabilityLegacy from './InputsSustainabilityLegacy';

import { formatSeparateByThousands } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';

export default function EventImpactInputs(props) {
    const [request, setRequest] = useState(false);
    const [inputsData, setInputsData] = useState({});
    const [linkedToBudget, setLinkedToBudget] = useState(false);
    const [event, setEvent] = useState({});

    const [osExpenditureTotal, setOsExpenditureTotal] = useState(0);
    const [osOtherTotal, setOsOtherTotal] = useState(0);

    useEffect(() => {
        fetchInputs();
    }, []);

    useEffect(() => {
        calculateTotals();
    }, [inputsData]);

    const calculateTotals = () => {
        if (inputsData.central_assumption && inputsData.expenditure) {
            let total = 0;
            let otherTotal = inputsData.central_assumption.event_financials.total_loc_expenditure - inputsData.central_assumption.workforce.total_spend;
            const data = inputsData.expenditure;

            Object.keys(data.os_expenditure).forEach((key) => {
                const entry = data.os_expenditure[key];
                if (_.isObject(entry)) {
                    Object.keys(entry).forEach((subKey) => {
                        const subEntry = entry[subKey];
                        if (subKey !== 'other' && key === 'event_production' && !linkedToBudget) {
                            otherTotal -= parseFloat(subEntry.value);
                        }
                    });
                }
            });

            Object.keys(data.os_expenditure).forEach((key) => {
                const entry = data.os_expenditure[key];
                if (_.isObject(entry)) {
                    Object.keys(entry).forEach((subKey) => {
                        if (subKey !== 'other') {
                            const subEntry = entry[subKey];
                            if (key === 'non_workforce_costs') {
                                if (linkedToBudget) {
                                    const departments = _.isArray(subEntry) ? {} : subEntry;
                                    Object.values(departments).forEach((departmentData) => {
                                        total += Math.round(departmentData.value * 100) / 100;
                                    });
                                }
                            } else if (key === 'event_production') {
                                if (!linkedToBudget) {
                                    total += parseFloat(subEntry.value);
                                }
                            } else {
                                total += parseFloat(subEntry.value);
                            }
                        }
                    });
                }
            });

            total += otherTotal;

            setOsOtherTotal(otherTotal);
            setOsExpenditureTotal(total);
        }
    };

    const fetchInputs = () => {
        setRequest(true);
        axios
            .get(props.fetchUrl)
            .then((response) => {
                const { inputs, event } = response.data;
                setEvent(event);
                setLinkedToBudget(event.impact_linked_to_budget);

                const data = {};
                Object.keys(inputs).forEach((key) => {
                    data[key] = inputs[key].data;
                });
                setInputsData(data);
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setRequest(false);
            });
    };

    const saveInputs = () => {
        setRequest(true);
        const data = { inputs: inputsData };

        axios
            .put(props.saveUrl, data)
            .then((response) => {
                const { inputs } = response.data;
                window.location.reload();
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setRequest(false);
            });
    };

    const setCentralAssumption = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.central_assumption = data;
        setInputsData(allData);
    };

    const setClientsGroups = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.client_groups = data;
        setInputsData(allData);
    };

    const setEventExpenditure = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.expenditure = data;
        setInputsData(allData);
    };

    const setPrePost = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.pre_post_event = data;
        setInputsData(allData);
    };

    const setReachAwareness = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.reach_awareness = data;
        setInputsData(allData);
    };

    const setEnvironmental = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.environmental = data;
        setInputsData(allData);
    };

    const setSustainability = (data) => {
        const allData = JSON.parse(JSON.stringify(inputsData));
        allData.sustainability_legacy = data;
        setInputsData(allData);
    };

    const changeLinkToBudget = (e) => {
        const value = !linkedToBudget;

        setLinkedToBudget(value);
        axios
            .put(props.toggleUrl, { value })
            .then((res) => {
                fetchInputs();
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const formatAndRound = (val) => {
        return isNaN(parseFloat(val)) ? `${val}` : formatSeparateByThousands(Math.round(val));
    };

    return (
        <div className="py-3">
            <div className={'mb-2 d-flex justify-content-end'}>
                <Button variant="primary" onClick={saveInputs} disabled={request}>
                    {$t('Save')}
                </Button>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-6">
                <div
                    className="in-nav horizontal"
                    data-tabs-active-classes="active"
                    data-tabs-inactive-classes="inactive"
                    data-default-tab-id="central-assumption-tab"
                    data-tabs-toggle="#impact-inputs-tab-content"
                    role="tablist"
                >
                    <button
                        className="in-nav__item"
                        id="central-assumption-tab"
                        data-tabs-target="#central-assumption"
                        type="button"
                        role="tab"
                        aria-controls="central-assumption"
                        aria-selected="true"
                    >
                        {$t('Central Assumptions')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="economic-client-groups-tab"
                        data-tabs-target="#economic-client-groups"
                        type="button"
                        role="tab"
                        aria-controls="economic-client-groups"
                        aria-selected="false"
                    >
                        {$t('Economic - Client Groups')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="economic-event-expenditure-tab"
                        data-tabs-target="#economic-event-expenditure"
                        type="button"
                        role="tab"
                        aria-controls="economic-event-expenditure"
                        aria-selected="false"
                    >
                        {$t('Economic - Event Expenditure')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="economic-pre-post-tab"
                        data-tabs-target="#economic-pre-post"
                        type="button"
                        role="tab"
                        aria-controls="economic-pre-post"
                        aria-selected="false"
                    >
                        {$t('Economic - Pre and Post Event')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="reach-awareness-tab"
                        data-tabs-target="#reach-awareness"
                        type="button"
                        role="tab"
                        aria-controls="reach-awareness"
                        aria-selected="false"
                    >
                        {$t('Reach & Awareness')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="environmental-tab"
                        data-tabs-target="#environmental"
                        type="button"
                        role="tab"
                        aria-controls="environmental"
                        aria-selected="false"
                    >
                        {$t('Environmental')}
                    </button>
                    <button
                        className="in-nav__item"
                        id="sustainability-legacy-tab"
                        data-tabs-target="#sustainability-legacy"
                        type="button"
                        role="tab"
                        aria-controls="sustainability-legacy"
                        aria-selected="false"
                    >
                        {$t('Social')}
                    </button>
                </div>

                <div id="impact-inputs-tab-content">
                    <div id="central-assumption" role="tabpanel" aria-labelledby="central-assumption-tab">
                        {inputsData.central_assumption && (
                            <InputsCentralAssumption
                                data={inputsData.central_assumption || {}}
                                changeHandler={setCentralAssumption}
                                event={event}
                                linkedToBudget={linkedToBudget}
                                changeLinkToBudget={changeLinkToBudget}
                                format={formatAndRound}
                            />
                        )}
                    </div>
                    <div id="economic-client-groups" role="tabpanel" aria-labelledby="economic-client-groups-tab">
                        {inputsData.client_groups && (
                            <InputsClientsGroups
                                data={inputsData.client_groups || {}}
                                changeHandler={setClientsGroups}
                                centralAssumption={inputsData.central_assumption}
                                visitorsSections={props.visitorsSections}
                                event={event}
                                format={formatAndRound}
                            />
                        )}
                    </div>
                    <div id="economic-event-expenditure" role="tabpanel" aria-labelledby="economic-event-expenditure-tab">
                        {inputsData.expenditure && inputsData.client_groups && (
                            <InputsEventExpenditure
                                data={inputsData.expenditure || {}}
                                changeHandler={setEventExpenditure}
                                totalEventExpenditure={osExpenditureTotal}
                                totalOther={osOtherTotal}
                                event={event}
                                format={formatAndRound}
                            />
                        )}
                    </div>
                    <div id="economic-pre-post" role="tabpanel" aria-labelledby="economic-pre-post-tab">
                        {inputsData.pre_post_event && (
                            <InputsPrePost data={inputsData.pre_post_event || {}} changeHandler={setPrePost} event={event} format={formatAndRound} />
                        )}
                    </div>
                    <div id="reach-awareness" role="tabpanel" aria-labelledby="reach-awareness-tab">
                        {inputsData.reach_awareness && (
                            <InputsReachAwareness
                                data={inputsData.reach_awareness || {}}
                                changeHandler={setReachAwareness}
                                event={event}
                                format={formatAndRound}
                            />
                        )}
                    </div>
                    <div id="environmental" role="tabpanel" aria-labelledby="environmental-tab">
                        {inputsData.environmental && (
                            <InputsEnvironmental data={inputsData.environmental || {}} changeHandler={setEnvironmental} event={event} format={formatAndRound} />
                        )}
                    </div>
                    <div id="sustainability-legacy" role="tabpanel" aria-labelledby="sustainability-legacy-tab">
                        {inputsData.sustainability_legacy && (
                            <InputsSustainabilityLegacy
                                data={inputsData.sustainability_legacy || {}}
                                changeHandler={setSustainability}
                                event={event}
                                format={formatAndRound}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

if (document.getElementById('EventImpactInputs')) {
    var rootEl = document.getElementById('EventImpactInputs');
    ReactDOM.render(
        <App>
            <EventImpactInputs fetchUrl={inputsFetchUrl} saveUrl={saveUrl} toggleUrl={toggleUrl} visitorsSections={JSON.parse(visitorsSections)} />
        </App>,
        rootEl,
    );
}
